import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import SnowflakeBoard from '../components/SnowflakeBoard';
import TridokuBoard from '../components/TridokuBoard';
import NumberKingdomsBoard from '../components/NumberKingdomsBoard';
import BattleshipBoard from '../components/BattleshipBoard';
import { getSolverBook } from '../data/solverBooks';
import {
  fetchBookStep,
  loadBookPuzzle,
  unlockBookSolver,
} from '../lib/api';
import { resolveDisplayImageUrl } from '../lib/assets';

const formatNumberList = (values) => {
  if (values.length === 0) {
    return '';
  }
  if (values.length === 1) {
    return String(values[0]);
  }
  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  }
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
};

export default function BookSolverPage() {
  const { bookCode = 'snowflake-sudoku' } = useParams();
  const solverBook = useMemo(() => getSolverBook(bookCode), [bookCode]);
  const [puzzleNumber, setPuzzleNumber] = useState('1');
  const [puzzleState, setPuzzleState] = useState(null);
  const [traceHistory, setTraceHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [accessKey, setAccessKey] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [loadingPuzzle, setLoadingPuzzle] = useState(false);
  const [loadingStep, setLoadingStep] = useState(false);

  const coverSrc = useMemo(
    () => resolveDisplayImageUrl(solverBook?.coverUrl),
    [solverBook]
  );
  const bookTitle = solverBook?.title || 'Book solver';
  const puzzleCount = solverBook?.puzzleCount || 360;
  const emptyValues = useMemo(
    () => Array.from({ length: solverBook?.cellCount || 42 }, () => 0),
    [solverBook]
  );

  useEffect(() => {
    setPuzzleNumber('1');
    setPuzzleState(null);
    setTraceHistory([]);
    setHistoryIndex(-1);
    setAccessKey('');
    setAccessToken('');
    setUnlocked(false);
    setError(null);
  }, [bookCode]);

  const clearTrace = () => {
    setPuzzleState(null);
    setTraceHistory([]);
    setHistoryIndex(-1);
  };

  const handleSolverError = (message) => {
    setError(message);
    if (/access|unlock|expired/i.test(message)) {
      setAccessToken('');
      setUnlocked(false);
      clearTrace();
    }
  };

  const unlockSolver = async (event) => {
    event.preventDefault();
    if (!solverBook) {
      return;
    }
    setUnlocking(true);
    setError(null);

    try {
      const response = await unlockBookSolver({
        solverFamily: solverBook.solverFamily,
        bookCode: solverBook.id,
        accessKey: accessKey.trim(),
      });
      setAccessToken(response.accessToken || '');
      setUnlocked(true);
      setAccessKey('');
      clearTrace();
    } catch (unlockError) {
      setAccessToken('');
      setUnlocked(false);
      clearTrace();
      setError(unlockError.message);
    } finally {
      setUnlocking(false);
    }
  };

  const loadPuzzle = async (event) => {
    event.preventDefault();
    if (!solverBook) {
      return;
    }
    if (!unlocked) {
      setError(`Unlock ${bookTitle} before loading puzzles.`);
      return;
    }

    setLoadingPuzzle(true);
    setError(null);

    try {
      const response = await loadBookPuzzle({
        solverFamily: solverBook.solverFamily,
        bookCode: solverBook.id,
        accessToken,
        puzzleNumber,
      });
      setPuzzleState(response);
      setTraceHistory([response]);
      setHistoryIndex(0);
    } catch (loadError) {
      setPuzzleState(null);
      setTraceHistory([]);
      setHistoryIndex(-1);
      handleSolverError(loadError.message);
    } finally {
      setLoadingPuzzle(false);
    }
  };

  const showNextStep = async () => {
    if (!solverBook || !puzzleState || !unlocked) {
      return;
    }

    setLoadingStep(true);
    setError(null);

    try {
      const nextHistoryIndex = historyIndex + 1;
      const response = await fetchBookStep({
        solverFamily: solverBook.solverFamily,
        bookCode: puzzleState.bookCode || solverBook.id,
        accessToken,
        puzzleNumber: puzzleState.puzzleNumber,
        stepIndex: puzzleState.nextStepIndex,
      });
      setPuzzleState(response);
      setTraceHistory((currentHistory) => [
        ...currentHistory.slice(0, nextHistoryIndex),
        response,
      ]);
      setHistoryIndex(nextHistoryIndex);
    } catch (stepError) {
      handleSolverError(stepError.message);
    } finally {
      setLoadingStep(false);
    }
  };

  const showPreviousStep = () => {
    if (historyIndex <= 0) {
      return;
    }

    const previousIndex = historyIndex - 1;
    const previousState = traceHistory[previousIndex];
    if (!previousState) {
      return;
    }

    setError(null);
    setPuzzleState(previousState);
    setHistoryIndex(previousIndex);
  };

  if (!solverBook) {
    return (
      <>
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          <section className="border-l-4 border-brand-magenta bg-white/90 p-6 shadow-xl shadow-slate-200/60">
            <Link
              to="/solver"
              className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-magenta transition hover:text-brand-cyan"
            >
              Solver
            </Link>
            <h1 className="mt-3 text-3xl font-semibold leading-tight text-slate-900">
              Book solver not found
            </h1>
            <p className="mt-3 text-sm leading-7 text-slate-600 md:text-base">
              Choose one of the available book solvers.
            </p>
          </section>
        </main>
      </>
    );
  }

  const currentStep = puzzleState?.step || null;
  const isNumberKingdoms = solverBook.solverFamily === 'number-kingdoms';
  const isBattleship = solverBook.solverFamily === 'battleship';
  const assignedCells = puzzleState?.highlights?.assignedCells || [];
  const eliminatedCells = puzzleState?.highlights?.eliminatedCells || [];
  const overlapCells = puzzleState?.highlights?.overlapCells || [];
  const patternCells = puzzleState?.highlights?.patternCells || currentStep?.patternCells || [];
  const sourceUnit = puzzleState?.highlights?.sourceUnit || currentStep?.sourceUnit || null;
  const peerUnit = puzzleState?.highlights?.peerUnit || currentStep?.peerUnit || null;
  const candidateDisplays = puzzleState?.candidateDisplays || [];
  const values = puzzleState?.values || emptyValues;
  const givens = puzzleState?.givens || [];
  const palette = puzzleState?.palette || puzzleState?.metadata?.palette || null;
  const stepCount = puzzleState?.stepCount || 0;
  const completedSteps = puzzleState ? Math.min(puzzleState.nextStepIndex, stepCount) : 0;
  const canShowNext =
    unlocked && Boolean(puzzleState) && !puzzleState.complete && !loadingPuzzle && !loadingStep;
  const canGoBack = historyIndex > 0 && !loadingPuzzle && !loadingStep;
  const eliminations =
    currentStep?.eliminations || (currentStep?.elimination ? [currentStep.elimination] : []);
  const assignments = currentStep?.assignments ||
    (currentStep?.assignment ? [currentStep.assignment] : []);
  const cellLabel = (cell) => puzzleState?.geometry?.cells?.find((entry) => entry.cell === cell)?.label;
  const removedValues = [...new Set(eliminations.map((elimination) => elimination.value))].sort(
    (first, second) => first - second
  );
  const removalCellCount = new Set(eliminations.map((elimination) => elimination.cell)).size;
  const removedValueText = formatNumberList(removedValues);
  const rawDifficultyLevel = puzzleState?.metadata?.difficultyLevel;
  const difficultyLevel =
    rawDifficultyLevel === undefined || rawDifficultyLevel === null || rawDifficultyLevel === ''
      ? null
      : Number(rawDifficultyLevel);
  const suppliedDifficultyStars = Number(
    puzzleState?.metadata?.difficultyStars ?? puzzleState?.metadata?.stars
  );
  const difficultyStars = Number.isFinite(suppliedDifficultyStars) && suppliedDifficultyStars > 0
    ? suppliedDifficultyStars
    : Number.isFinite(difficultyLevel)
    ? difficultyLevel + 1
    : null;
  const difficultyLabel = difficultyStars
    ? `${difficultyStars}-${difficultyStars === 1 ? 'star' : 'stars'}`
    : 'unknown';

  return (
    <>
      <Header />
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6">
        <section className="grid gap-5 border-l-4 border-brand-cyan bg-white/85 p-5 shadow-xl shadow-slate-200/60 md:grid-cols-[minmax(0,1fr)_9rem] md:items-center md:p-6">
          <div>
            <Link
              to="/solver"
              className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-cyan transition hover:text-brand-magenta"
            >
              Solver
            </Link>
            <h1 className="mt-3 text-3xl font-semibold leading-tight text-slate-900 md:text-[2.35rem]">
              {bookTitle}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 md:text-base">
              Select a puzzle number and step through the solution.
            </p>
          </div>
          {coverSrc ? (
            <img
              src={coverSrc}
              alt={bookTitle}
              className="hidden max-h-40 w-auto justify-self-end rounded-md border border-slate-200 bg-white p-2 shadow-sm md:block"
            />
          ) : null}
        </section>

        {!unlocked ? (
          <section className="max-w-xl border border-slate-200 bg-white/90 p-5 shadow-lg shadow-slate-200/50">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
              Access
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
              Unlock {bookTitle}
            </h2>
            <form onSubmit={unlockSolver} className="mt-5 space-y-4">
              <div className="text-sm font-medium text-slate-700">
                <label htmlFor={`${solverBook.id}-access-key`}>Access key</label>
                <div className="mt-2 flex rounded-md border border-slate-300 bg-white shadow-inner shadow-slate-100 focus-within:border-brand-cyan focus-within:ring-4 focus-within:ring-brand-cyan/20">
                  <input
                    id={`${solverBook.id}-access-key`}
                    type="text"
                    value={accessKey}
                    onChange={(event) => setAccessKey(event.target.value)}
                    autoComplete="off"
                    className="min-w-0 flex-1 rounded-md border-0 bg-transparent px-3 py-2 text-base text-slate-900 outline-none"
                  />
                </div>
              </div>

              {error ? (
                <div className="border border-rose-200 bg-rose-50 p-3 text-sm leading-6 text-rose-700">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={unlocking || !accessKey.trim()}
                className="w-full rounded-md bg-brand-cyan px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-magenta disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {unlocking ? 'Unlocking' : 'Unlock solver'}
              </button>
            </form>
          </section>
        ) : (
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_21rem]">
          <section className="min-w-0 space-y-5">
            <form
              onSubmit={loadPuzzle}
              className="grid grid-cols-[minmax(0,1fr)_8rem] items-end gap-2 border border-slate-200 bg-white/90 p-3 shadow-sm sm:grid-cols-[minmax(0,1fr)_10rem] sm:gap-3"
            >
              <label className="flex-1 text-sm font-medium text-slate-700">
                <span>Puzzle number</span>
                <input
                  type="number"
                  min="1"
                  max={puzzleCount}
                  value={puzzleNumber}
                  onChange={(event) => setPuzzleNumber(event.target.value)}
                  className="mt-1.5 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 shadow-inner shadow-slate-100 focus:border-brand-cyan focus:outline-none focus:ring-4 focus:ring-brand-cyan/20"
                />
              </label>
              <button
                type="submit"
                disabled={loadingPuzzle}
                className="h-11 rounded-md bg-slate-900 px-3 text-sm font-semibold text-white transition hover:bg-brand-magenta disabled:cursor-not-allowed disabled:opacity-60 sm:px-5"
              >
                {loadingPuzzle ? 'Loading' : 'Load puzzle'}
              </button>
            </form>

            <div className="overflow-hidden border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {puzzleState ? `Puzzle ${puzzleState.puzzleNumber}` : 'Puzzle board'}
                  </h2>
                  {puzzleState ? (
                    <span className="text-sm font-medium text-slate-500">
                      {completedSteps} / {stepCount} steps
                    </span>
                  ) : null}
                </div>
              </div>
              <div className={isBattleship ? 'mx-auto max-w-xl p-3 sm:p-4' : isNumberKingdoms ? 'p-3 sm:p-4' : 'mx-auto max-w-xl p-4 sm:p-6'}>
                {isBattleship ? (
                  <BattleshipBoard
                    board={puzzleState?.board}
                    givens={givens}
                    rowTotals={puzzleState?.rowTotals}
                    columnTotals={puzzleState?.columnTotals}
                    fleet={puzzleState?.fleet}
                    changedCells={puzzleState?.highlights?.changedCells || []}
                    eliminatedCandidates={puzzleState?.highlights?.eliminatedCandidates || []}
                    excludedCandidates={puzzleState?.excludedCandidates || []}
                    difficultyName={puzzleState?.metadata?.difficultyName || 'Unrated'}
                    ariaLabel={`${bookTitle} Puzzle ${puzzleState?.puzzleNumber || ''}`.trim()}
                  />
                ) : isNumberKingdoms ? (
                  <NumberKingdomsBoard
                    geometry={puzzleState?.geometry}
                    values={values}
                    givens={givens}
                    candidates={puzzleState?.candidates || []}
                    candidateDisplays={candidateDisplays}
                    assignedCells={assignedCells}
                    eliminatedCells={eliminatedCells}
                    patternCells={patternCells}
                    sourceUnit={sourceUnit}
                    ariaLabel={`${bookTitle} Puzzle ${puzzleState?.puzzleNumber || ''}`.trim()}
                  />
                ) : solverBook.solverFamily === 'tridoku' ? (
                  <TridokuBoard
                    values={values}
                    givens={givens}
                    assignedCells={assignedCells}
                    eliminatedCells={eliminatedCells}
                    overlapCells={overlapCells}
                    patternCells={patternCells}
                    candidateDisplays={candidateDisplays}
                    sourceUnit={sourceUnit}
                    peerUnit={peerUnit}
                    palette={palette}
                    ariaLabel={`${bookTitle} Puzzle ${puzzleState?.puzzleNumber || ''}`.trim()}
                  />
                ) : (
                  <SnowflakeBoard
                    values={values}
                    givens={givens}
                    assignedCells={assignedCells}
                    eliminatedCells={eliminatedCells}
                    overlapCells={overlapCells}
                    candidateDisplays={candidateDisplays}
                  />
                )}
              </div>
            </div>
          </section>

          <aside className={`min-w-0 space-y-4${isBattleship ? ' lg:sticky lg:top-4 lg:self-start' : ''}`}>
            <div className="flex flex-col border border-slate-200 bg-white/90 p-5 shadow-sm">
              <div aria-live="polite" aria-atomic="true" className="order-2 mt-4 h-48 overflow-y-auto pr-1 lg:order-1 lg:mt-0 lg:h-56">
                {error ? (
                  <div className="border border-rose-200 bg-rose-50 p-3 text-sm leading-6 text-rose-700">
                    {error}
                  </div>
                ) : null}

                {currentStep ? (
                  <div className="space-y-3">
                    <div className="border-l-4 border-brand-cyan bg-slate-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {currentStep.techniqueLabel}
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate-700">
                        {currentStep.explanation}
                      </p>
                    </div>

                    {isBattleship ? (
                      <p className="text-sm leading-6 text-slate-600">
                        {currentStep.changes?.length
                          ? `${currentStep.changes.length} ${currentStep.changes.length === 1 ? 'cell changes' : 'cells change'} in this step. Blue outlines show where.`
                          : 'The board stays the same while one possible ship placement is ruled out.'}
                      </p>
                    ) : isNumberKingdoms ? (
                      <>
                        {assignments.length || eliminations.length ? (
                          <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
                            {assignments.map((assignment) => (
                              <li key={`place-${assignment.cell}-${assignment.value}`}>Place {assignment.value} in {cellLabel(assignment.cell)}.</li>
                            ))}
                            {eliminations.map((elimination) => (
                              <li key={`remove-${elimination.cell}-${elimination.value}`}>Remove candidate {elimination.value} from {cellLabel(elimination.cell)}.</li>
                            ))}
                          </ul>
                        ) : null}
                        {currentStep.requiresPriorEliminations ? (
                          <p className="text-sm leading-6 text-slate-600">This deduction uses candidates removed in earlier steps. Back restores those earlier candidate states.</p>
                        ) : null}
                      </>
                    ) : currentStep.assignment ? (
                      <div className="text-sm leading-6 text-slate-700">
                        Place{' '}
                        <span className="font-semibold text-brand-magenta">
                          {currentStep.assignment.value}
                        </span>{' '}
                        in the highlighted cell.
                      </div>
                    ) : null}

                    {!isNumberKingdoms && !isBattleship && eliminations.length > 0 ? (
                      <div className="text-sm leading-6 text-slate-700">
                        Remove {removedValueText} from the yellow highlighted{' '}
                        {removalCellCount === 1 ? "cell's" : "cells'"} candidates.
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm leading-6 text-slate-600">
                    {puzzleState ? 'The printed clues are loaded. Choose Next for the first deduction.' : `Load a ${bookTitle} puzzle to begin the trace.`}
                  </p>
                )}

                {puzzleState?.complete ? (
                  <div className="mt-4 border border-emerald-200 bg-emerald-50 p-3 text-sm leading-6 text-emerald-800">
                    {puzzleState.status === 'solved'
                      ? isBattleship ? 'Fleet found. Every ship and water cell is accounted for.' : 'The trace is complete.'
                      : puzzleState.explanation || 'The trace is complete.'}
                  </div>
                ) : null}
              </div>

              <div className="order-1 grid grid-cols-2 gap-2 lg:order-2 lg:mt-4">
                <button
                  type="button"
                  onClick={showPreviousStep}
                  disabled={!canGoBack}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-brand-cyan hover:text-brand-cyan focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-cyan disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={showNextStep}
                  disabled={!canShowNext}
                  className="rounded-md bg-brand-cyan px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-magenta focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-magenta disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {loadingStep ? 'Checking' : 'Next'}
                </button>
              </div>
            </div>

            {isBattleship ? (
              <details className="border border-slate-200 bg-white/90 p-5 text-sm leading-6 text-slate-600 shadow-sm">
                <summary className="cursor-pointer font-semibold text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-cyan">Battleship rules</summary>
                <p className="mt-3">Find ten straight ships: one of length 4, two of length 3, three of length 2, and four submarines. Ships run across or down and never touch, even at a corner.</p>
                <p className="mt-2">The numbers above and to the right of the grid count occupied cells in each column and row. Printed ship parts and water are fixed clues.</p>
                <p className="mt-2">A round end points away from the rest of its ship. A solid square is a middle, with ship cells on two opposite sides. A striped square is occupied, but its exact part is still undecided.</p>
                <p className="mt-2">Follow one deduction at a time with Next. Some deductions rule out a whole possible ship position without changing any cells. Back returns to the previous step.</p>
              </details>
            ) : null}

            {isNumberKingdoms ? (
              <details className="border border-slate-200 bg-white/90 p-5 text-sm leading-6 text-slate-600 shadow-sm">
                <summary className="cursor-pointer font-semibold text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-cyan">Number Kingdoms rules</summary>
                <p className="mt-3">Fill each outlined kingdom with digits 1–8 exactly once. Leave the dark stones empty.</p>
                <p className="mt-2">Equal digits cannot share a clear row, column, or diagonal, or sit a chess knight move apart (two squares one way and one square across).</p>
                <p className="mt-2">Only stones block sight lines. Digits and kingdom boundaries do not. Stones never block a knight move or cancel the kingdom rule.</p>
                <p className="mt-2">Short sight lines need no repeats; they do not have to contain every digit.</p>
              </details>
            ) : null}

            {!isBattleship && puzzleState?.metadata ? (
              <div className="border border-slate-200 bg-white/90 p-5 text-sm leading-6 text-slate-600 shadow-sm">
                <p className="font-semibold text-slate-900">Puzzle profile</p>
                <p className="mt-2">Difficulty: {difficultyLabel}</p>
                <p>
                  Hardest technique:{' '}
                  {puzzleState.metadata.hardestTechniqueLabel ||
                    String(puzzleState.metadata.hardestTechnique || 'unknown').replaceAll(
                      '_',
                      ' '
                    )}
                </p>
              </div>
            ) : null}
          </aside>
        </div>
        )}
      </main>
    </>
  );
}
